/**
 * Private/local address classification — the SYNTACTIC half of the two-layer SSRF guard
 * described in `apps/api/src/crawler/net-guard.ts`'s doc comment. Pure, synchronous, no Node/
 * Bun runtime assumption (no `node:dns`, no `node:net`) so it can be imported from
 * `packages/validation` (Zod schemas, browser-safe) as well as from `apps/api`.
 *
 * **This module alone is NOT a security boundary.** A hostname string can resolve to any
 * address its owner's DNS chooses, today or after this check ran — see `net-guard.ts` for the
 * authoritative, DNS-resolving layer that actually gates outbound crawler fetches. What lives
 * here exists to give a fast, friendly Zod error at input time (source-detail-diagnostics-style
 * UX: "this URL is obviously local, reject with a clear message" before ever queuing a job) and
 * to double as the cheap first check inside the fetch-time guard, so an obviously-bad literal
 * never even pays for a DNS round trip.
 *
 * Classification is deliberately run on `new URL(value).hostname`, never on the raw input
 * string — the WHATWG URL parser already canonicalizes the classic numeric-IPv4 obfuscation
 * tricks (decimal `2130706433`, octal `0177.0.0.1`, hex `0x7f.0.0.1`, shortened `127.1`) into
 * plain dotted-quad form, and canonicalizes IPv4-mapped IPv6 addresses (`::ffff:10.0.0.1`) into
 * their hex-group form — both confirmed against Bun's URL implementation before writing this.
 * Callers MUST extract the hostname the same way (`new URL(value).hostname`) for that
 * normalization to apply; classifying an un-parsed raw string is not equivalent.
 */

/** [network address, prefix length] pairs, in dotted-quad form for readability. Beyond the
 * task's explicit range list (loopback/this-network/RFC1918/link-local/CGNAT), this also
 * covers the other IANA special-purpose ranges a real attacker would reach for (2026-08-12
 * security review, M3): none of these are "the internet" either, and several are live SSRF
 * targets in practice (Oracle Cloud's metadata service sits at 192.0.0.192, inside the IETF
 * Protocol Assignments block below). */
const PRIVATE_IPV4_RANGES: ReadonlyArray<readonly [string, number]> = [
  ['127.0.0.0', 8], // loopback
  ['0.0.0.0', 8], // "this network" / unspecified
  ['10.0.0.0', 8], // RFC1918
  ['172.16.0.0', 12], // RFC1918
  ['192.168.0.0', 16], // RFC1918
  ['169.254.0.0', 16], // link-local — includes the cloud metadata endpoint 169.254.169.254
  ['100.64.0.0', 10], // CGNAT (RFC6598)
  ['192.0.0.0', 24], // IETF Protocol Assignments (RFC6890) — includes Oracle's 192.0.0.192 metadata
  ['198.18.0.0', 15], // Benchmarking (RFC2544)
  ['192.88.99.0', 24], // deprecated 6to4 relay anycast (RFC3068) — still a special, non-internet range
  ['224.0.0.0', 4], // multicast
  ['240.0.0.0', 4], // reserved for future use — also covers 255.255.255.255, the broadcast address
];

/** Parses a dotted-quad IPv4 literal (each octet 0-255, no leading-zero ambiguity beyond what
 * `Number()` accepts) into its 32-bit integer form. `null` for anything that isn't exactly four
 * plain decimal octets — deliberately strict: by the time this runs, `new URL().hostname` has
 * already normalized decimal/octal/hex obfuscation into plain dotted-quad, so this function
 * doesn't need to (and shouldn't — re-parsing octal/hex here would just reintroduce the same
 * ambiguity the URL parser exists to remove). */
function parseIPv4(ip: string): number | null {
  const octets = ip.split('.');
  if (octets.length !== 4) return null;
  let value = 0;
  for (const octet of octets) {
    if (!/^\d{1,3}$/.test(octet)) return null;
    const n = Number(octet);
    if (n > 255) return null;
    value = (value << 8) | n;
  }
  return value >>> 0;
}

export function isPrivateIPv4(ip: string): boolean {
  const value = parseIPv4(ip);
  if (value === null) return false;
  return PRIVATE_IPV4_RANGES.some(([base, prefixLength]) => {
    const baseValue = parseIPv4(base);
    if (baseValue === null) return false;
    const mask = prefixLength === 0 ? 0 : (~0 << (32 - prefixLength)) >>> 0;
    return (value & mask) === (baseValue & mask);
  });
}

/** Splits an IPv6 literal (no brackets, optional `%zone` suffix) into 8 16-bit groups,
 * expanding a single `::` compression and an embedded trailing IPv4 dotted-quad (the
 * IPv4-mapped/IPv4-compatible forms). `null` for anything unparseable. */
function parseIPv6Groups(literal: string): number[] | null {
  const address = literal.split('%')[0] ?? literal;
  const parts = address.split('::');
  if (parts.length > 2) return null;

  function parseSide(side: string): number[] | null {
    if (side === '') return [];
    const segments = side.split(':');
    const groups: number[] = [];
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (segment === undefined) return null;
      if (segment.includes('.')) {
        // An embedded IPv4 dotted-quad is only legal as the LAST segment.
        if (i !== segments.length - 1) return null;
        const v4 = parseIPv4(segment);
        if (v4 === null) return null;
        groups.push((v4 >>> 16) & 0xffff, v4 & 0xffff);
        continue;
      }
      if (!/^[0-9a-fA-F]{1,4}$/.test(segment)) return null;
      groups.push(Number.parseInt(segment, 16));
    }
    return groups;
  }

  if (parts.length === 1) {
    const groups = parseSide(parts[0] ?? '');
    return groups && groups.length === 8 ? groups : null;
  }

  const head = parseSide(parts[0] ?? '');
  const tail = parseSide(parts[1] ?? '');
  if (!head || !tail) return null;
  const missing = 8 - head.length - tail.length;
  if (missing < 0) return null;
  return [...head, ...new Array(missing).fill(0), ...tail];
}

export function isPrivateIPv6(literal: string): boolean {
  const groups = parseIPv6Groups(literal);
  if (!groups) return false;

  const isAllZero = groups.every((g) => g === 0);
  // `::` — the unspecified address. Not independently listed in the task's range table (that
  // lists `0.0.0.0/8` for v4), but it's the direct v6 analogue and just as useless as a
  // "somewhere on the internet" destination, so it's rejected for the same reason.
  if (isAllZero) return true;

  // `::1` — loopback.
  if (groups.slice(0, 7).every((g) => g === 0) && groups[7] === 1) return true;

  // `fc00::/7` — unique local addresses (includes the commonly-used `fd00::/8`).
  if (((groups[0] ?? 0) & 0xfe00) === 0xfc00) return true;

  // `fe80::/10` — link-local.
  if (((groups[0] ?? 0) & 0xffc0) === 0xfe80) return true;

  // `fec0::/10` — site-local (RFC3879 deprecated it in 2004, but deprecated is not the same as
  // unroutable/rejected everywhere, and it's the direct v6 analogue of RFC1918's private
  // ranges — treat it the same way `fe80::/10` right above is treated. Same mask, adjacent
  // 10-bit prefix (`0xfe80` vs `0xfec0`), deliberately checked as its own branch rather than
  // widening `fe80`'s mask, since folding the two together would also start matching addresses
  // in between that belong to neither range.
  if (((groups[0] ?? 0) & 0xffc0) === 0xfec0) return true;

  // `ff00::/8` — multicast. Not a v4-embedding bypass like the branches below; just not a
  // meaningful unicast destination for a crawler to fetch, same reasoning as `224.0.0.0/4` in
  // the v4 table.
  if (((groups[0] ?? 0) & 0xff00) === 0xff00) return true;

  const g = (i: number) => groups[i] ?? 0;
  /** Builds a dotted-quad from two 16-bit groups and recurses into the v4 range table — shared
   * by every "this v6 literal actually carries a v4 address" form below, so a private embedded
   * address is caught the same way regardless of which encoding smuggled it in. */
  const embeddedIPv4 = (hi: number, lo: number): boolean =>
    isPrivateIPv4(`${(hi >> 8) & 0xff}.${hi & 0xff}.${(lo >> 8) & 0xff}.${lo & 0xff}`);

  // `::ffff:0:0/96` — IPv4-mapped IPv6, the classic bypass: a literal that READS as v6 but
  // carries a v4 address (often a private one) in its low 32 bits.
  if (g(0) === 0 && g(1) === 0 && g(2) === 0 && g(3) === 0 && g(4) === 0 && g(5) === 0xffff) {
    return embeddedIPv4(g(6), g(7));
  }

  // `::ffff:0:0:0/96` — IPv4-translated (RFC8215's companion form to IPv4-mapped, used
  // alongside NAT64 deployments). Same idea, one group further out.
  if (g(0) === 0 && g(1) === 0 && g(2) === 0 && g(3) === 0 && g(4) === 0xffff && g(5) === 0) {
    return embeddedIPv4(g(6), g(7));
  }

  // `::/96` — IPv4-compatible IPv6 (deprecated, RFC4291's predecessor to IPv4-mapped, but still
  // parses and still routes on stacks that honor it). All zero for the first 96 bits, v4 in the
  // low 32. `::1`/`::` themselves are already handled above and never reach this branch.
  if (g(0) === 0 && g(1) === 0 && g(2) === 0 && g(3) === 0 && g(4) === 0 && g(5) === 0) {
    return embeddedIPv4(g(6), g(7));
  }

  // `64:ff9b::/96` — the well-known NAT64 prefix (RFC6052): a NAT64 gateway synthesizes one of
  // these per IPv4 destination, so this is a direct v4-reachability channel from anything that
  // can be told to resolve/connect through it.
  if (g(0) === 0x0064 && g(1) === 0xff9b && g(2) === 0 && g(3) === 0 && g(4) === 0 && g(5) === 0) {
    return embeddedIPv4(g(6), g(7));
  }

  // `64:ff9b:1::/48` — RFC8215's LOCAL-USE NAT64 prefix, a sibling of the well-known one right
  // above (same first 32 bits, `64:ff9b`, but a fixed third group of `1` instead of `0`,
  // reserved for network operators to run their own NAT64 rather than the well-known global
  // one). Same v4-reachability channel, same reasoning — blocking one and not the other was
  // the exact asymmetry flagged in review. Simplification, stated rather than hidden: RFC8215
  // allows several prefix-length variants (32/40/48/56/64/96) with the v4 address's bits
  // interleaved with PSID bits depending on which; this only handles the common case of the
  // v4 address sitting in the last two groups, same as the /96 branch above and the NAT64
  // branch it mirrors — a shorter-prefix variant that places v4 bits elsewhere is not decoded.
  if (g(0) === 0x0064 && g(1) === 0xff9b && g(2) === 1) {
    return embeddedIPv4(g(6), g(7));
  }

  // `2002::/16` — 6to4 (RFC3056): the v4 address is embedded directly in bits 16-48, i.e. the
  // next two groups, not the low 32 bits.
  if (g(0) === 0x2002) {
    return embeddedIPv4(g(1), g(2));
  }

  // `2001::/32` — Teredo (RFC4380/8190). The security-relevant address here is the CLIENT's
  // embedded v4 (what a Teredo-relayed packet actually reaches), which sits in the last 32
  // bits — but XOR-obscured with `0xffff`, specifically so NAT/firewall devices along the path
  // don't mistake it for a literal routable address, not for any security purpose of its own.
  // Un-obscure before classifying, rather than checking the raw bits (which would silently miss
  // every private client address, since the obscured form essentially never LOOKS private).
  if (g(0) === 0x2001 && g(1) === 0) {
    return embeddedIPv4(g(6) ^ 0xffff, g(7) ^ 0xffff);
  }

  return false;
}

/** Classifies a bare IP literal (no brackets) as private, trying v4 then v6 — the shape DNS
 * resolution results and `net.isIP`-style address strings come in. Use `isPrivateOrLocalHostname`
 * instead when the input might be a bracketed IPv6 literal or a hostname. */
export function isPrivateAddress(literal: string): boolean {
  if (isPrivateIPv4(literal)) return true;
  if (literal.includes(':')) return isPrivateIPv6(literal);
  return false;
}

const LOCAL_HOSTNAME_SUFFIXES = ['.local', '.internal', '.localhost'];

/**
 * True when `hostnameRaw` (as returned by `new URL(value).hostname`) IS a literal IP address —
 * bracketed IPv6 or plain dotted-quad IPv4 — rather than a name that needs resolving.
 *
 * Exists for `net-guard.ts` (2026-08-12 security review, M1): a literal IP has nothing to
 * resolve, and asking a DNS resolver to "look up" one is at best a no-op and at worst a
 * rejection — Node's `dns.lookup` does not accept a bracketed IPv6 literal, so a fetch-time
 * guard that unconditionally resolved every hostname was refusing perfectly legitimate public
 * IPv6-literal URLs (`https://[2001:4860:4860::8888]/`) with `ENOTFOUND`, even though layer 1
 * (`isPrivateOrLocalHostname`, right below) already accepts that same URL. Callers should check
 * this AFTER `isPrivateOrLocalHostname` (not instead of it) and, when true, classify the literal
 * directly via `isPrivateAddress` rather than resolving it.
 */
export function isIpLiteralHostname(hostnameRaw: string): boolean {
  let hostname = hostnameRaw.trim().toLowerCase();
  if (hostname.endsWith('.')) hostname = hostname.slice(0, -1);

  if (hostname.startsWith('[') && hostname.endsWith(']')) {
    return parseIPv6Groups(hostname.slice(1, -1)) !== null;
  }
  return parseIPv4(hostname) !== null;
}

/**
 * The syntactic layer-1 check: given a hostname AS RETURNED BY `new URL(value).hostname`
 * (lowercased already by the URL parser; IPv6 literals come back bracketed, e.g. `"[::1]"`),
 * decides whether it is obviously private/loopback/link-local/local-only.
 *
 * Not a security boundary on its own — see this file's top-level doc comment.
 */
export function isPrivateOrLocalHostname(hostnameRaw: string): boolean {
  let hostname = hostnameRaw.trim().toLowerCase();
  if (hostname.endsWith('.')) hostname = hostname.slice(0, -1); // trailing-dot FQDN root

  if (hostname.startsWith('[') && hostname.endsWith(']')) {
    return isPrivateIPv6(hostname.slice(1, -1));
  }

  if (isPrivateIPv4(hostname)) return true;

  if (hostname === 'localhost') return true;
  return LOCAL_HOSTNAME_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}
